import React, { useState, useEffect } from 'react';
import { FiUserPlus, FiRefreshCw } from 'react-icons/fi';
import { StudentFlags } from './StudentFlags';
import { useMoodleBadgeFlags } from './StudentFlags';
import { useLoading } from '../LoadingContext';

const TaskInfoPanel = ({ isVisible, onClose, barId, barLabel = 'Unbekannt', onOpenSchuelerModal, onHeightChange, assignedStudents = [], onStudentsUpdate, taskData = null, onTaskDataUpdate, user = null }) => {
  const [students, setStudents] = useState(assignedStudents);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [refreshSeed, setRefreshSeed] = useState(0);
  
  // Global Loading Context
  const { showLoading, hideLoading } = useLoading();
  
  // Prüfe ob aktueller Benutzer ein Lernender ist
  const isLearner = user && user.isLearner === true;
  
  // Für Lernende: Filtere die Studentenliste, um nur den aktuell angemeldeten Lernenden zu zeigen
  const filteredStudents = React.useMemo(() => {
    if (!isLearner) {
      return students; // Lehrpersonen sehen alle zugewiesenen Lernenden
    }
    
    // Lernende sehen nur sich selbst, falls sie dem Task zugewiesen sind
    const currentUserLogin = user?.username || user?.login || user?.id;
    const matchingStudents = students.filter(student => 
      student.login === currentUserLogin || 
      student.username === currentUserLogin ||
      student.id === currentUserLogin
    );
    
    // Falls der Lernende nicht in der Zuordnungsliste ist, zeige trotzdem ein Mock-Entry
    // damit er seine potenziellen Badges sehen kann
    if (matchingStudents.length === 0 && currentUserLogin) {
      return [{
        id: currentUserLogin,
        login: currentUserLogin,
        username: currentUserLogin,
        vorname: user?.name?.split(' ')[0] || 'Aktueller',
        nachname: user?.name?.split(' ').slice(1).join(' ') || 'Benutzer',
        klasse: 'Nicht zugeordnet',
        email: user?.email || '',
        // Markiere als nicht-zugeordnet für spezielles Styling
        isCurrentUser: true,
        isNotAssigned: true
      }];
    }
    
    return matchingStudents;
  }, [students, isLearner, user]);
  
  // Hook für echte Moodle Badge-Daten - verwende gefilterte Studentenliste
  const { 
    userBadgeData, 
    loading: badgeLoading, 
    error: badgeError, 
    refreshFlags 
  } = useMoodleBadgeFlags(taskData, filteredStudents);

  // Synchronisiere mit den von außen kommenden assignedStudents
  useEffect(() => {
    setStudents(assignedStudents);
    // Reset sort when students change
    setSortConfig({ key: null, direction: 'asc' });
  }, [assignedStudents]);

  // Global Loading Spinner für Badge-Loading
  useEffect(() => {
    if (badgeLoading && taskData?.linkEvidence && taskData?.evidenceType === 'moodle-badge') {
      showLoading('Lade Badge-Daten von Moodle...');
    } else {
      hideLoading();
    }
    
    // Cleanup beim Unmount oder wenn Panel geschlossen wird
    return () => {
      if (!isVisible) {
        hideLoading();
      }
    };
  }, [badgeLoading, taskData, isVisible, showLoading, hideLoading]);

  useEffect(() => {
    // Task data change tracking removed for performance
  }, [taskData, barId, filteredStudents.length]);

  useEffect(() => {
    // Students state change tracking removed for performance
    
    // Berechne die dynamische Höhe basierend auf der Anzahl der Studenten
    const calculatePanelHeight = () => {
      const headerHeight = 50; // Header mit Titel und Button
      const baseContentHeight = 20; // Padding und Grundhöhe
      
      if (students.length === 0) {
        // Nur die "Keine Lernenden" Nachricht
        return headerHeight + baseContentHeight + 60; // ~130px
      } else {
        // Tabelle: Header und Zeilen mit empirisch gemessenen Höhen
        const tableHeaderHeight = 37; // Empirisch gemessen
        const rowHeight = 31; // Empirisch gemessen
        const studentsHeight = students.length * rowHeight;
        return headerHeight + baseContentHeight + tableHeaderHeight + studentsHeight + 20; // Extra padding
      }
    };
    
    const newHeight = calculatePanelHeight();
    
    // Informiere die Parent-Komponente über die Höhenänderung
    // Verwende setTimeout um sicherzustellen, dass alle DOM-Updates abgeschlossen sind
    if (onHeightChange) {
      setTimeout(() => {
        onHeightChange(barId, newHeight);
      }, 10); // Kleine Verzögerung für DOM-Updates
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredStudents, students.length, barId]); // onHeightChange absichtlich ausgelassen um Endlosschleife zu vermeiden

  // Informiere die Parent-Komponente über Höhenänderung wenn Panel ausgeblendet wird
  useEffect(() => {
    if (!isVisible && onHeightChange) {
      // Setze Höhe sofort auf 0 wenn Panel geschlossen wird
      onHeightChange(barId, 0);
      // Verstecke Loading-Spinner wenn Panel geschlossen wird
      hideLoading();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, barId]); // onHeightChange und hideLoading absichtlich ausgelassen um Endlosschleife zu vermeiden

  if (!isVisible) return null;

  // Sortierfunktion
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sortedStudents = [...students].sort((a, b) => {
      if (a[key] < b[key]) {
        return direction === 'asc' ? -1 : 1;
      }
      if (a[key] > b[key]) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    setStudents(sortedStudents);
  };

  // Funktion zum Rendern des Sortier-Icons
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return '↕️'; // Neutral icon
    }
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const handleOpenSchuelerModal = () => {
    if (onOpenSchuelerModal) {
      onOpenSchuelerModal(barId, (selectedLernendeIds, lernendeData, actionMode = 'add', klassensynchronisation = '') => {
        
        let updatedStudents = [...students];
        
        if (actionMode === 'add') {
          // Filtere die ausgewählten Lernenden aus den geladenen Daten
          const selectedStudents = lernendeData.filter(lernender => 
            selectedLernendeIds.includes(lernender.id)
          );
          
          
          // Füge neue Lernende hinzu, aber vermeide Duplikate
          const existingIds = new Set(students.map(s => s.id));
          const newStudents = selectedStudents.filter(s => !existingIds.has(s.id));
          
          updatedStudents = [...students, ...newStudents];
          
        } else if (actionMode === 'remove') {
          // Entferne die ausgewählten Lernenden
          const idsToRemove = new Set(selectedLernendeIds);
          updatedStudents = students.filter(student => !idsToRemove.has(student.id));
        }
        
        // Aktualisiere die lokalen Studenten
        setStudents(updatedStudents);
        
        // Informiere die Parent-Komponente über die Änderung für Persistierung
        // Verwende den actionMode um zu bestimmen, ob additive oder vollständige Aktualisierung
        if (onStudentsUpdate) {
          onStudentsUpdate(barId, updatedStudents, actionMode);
        }
        
        // Informiere die Parent-Komponente über Klassensynchronisation Änderungen
        if (onTaskDataUpdate && klassensynchronisation !== undefined) {
          onTaskDataUpdate(barId, { klassensynchronisation });
        }
      });
    }
  };

  const handleRefreshBadges = async () => {
    setRefreshSeed(prev => prev + 1);
    showLoading('Aktualisiere Badge-Daten...');
    try {
      await refreshFlags();
    } finally {
      // hideLoading wird durch den useEffect basierend auf badgeLoading gesteuert
      // Hier verwenden wir setTimeout um sicherzustellen, dass der Spinner mindestens kurz sichtbar ist
      setTimeout(() => {
        if (!badgeLoading) {
          hideLoading();
        }
      }, 500);
    }
  };

  return (
    <div 
      style={{
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        minWidth: '300px',
        backgroundColor: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '4px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        zIndex: 100,
        marginTop: '2px'
      }}
    >
      <div
        style={{
          padding: '12px',
          borderBottom: '1px solid #dee2e6',
          backgroundColor: '#e9ecef',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Lernende verwalten Button - nur für Lehrpersonen sichtbar */}
          {!isLearner && (
            <button
              onClick={handleOpenSchuelerModal}
              style={{
                background: 'rgba(0, 123, 255, 0.1)',
                border: '1px solid #007bff',
                borderRadius: '4px',
                color: '#007bff',
                cursor: 'pointer',
                padding: '4px 6px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Lernende verwalten"
            >
              <FiUserPlus size={14} />
            </button>
          )}
          <h6 style={{ margin: 0, fontSize: '14px', fontWeight: 'bold' }}>
            {isLearner ? 'Mein Fortschritt für:' : 'Fortschritte für:'} {barLabel}
          </h6>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Refresh Button - nur anzeigen wenn es Moodle Badge Evidence gibt */}
          {taskData?.evidenceType === 'moodle-badge' && taskData?.linkEvidence && (
            <button
              onClick={handleRefreshBadges}
              disabled={badgeLoading}
              style={{
                background: badgeLoading ? 'rgba(108, 117, 125, 0.1)' : 'rgba(40, 167, 69, 0.1)',
                border: badgeLoading ? '1px solid #6c757d' : '1px solid #28a745',
                borderRadius: '4px',
                color: badgeLoading ? '#6c757d' : '#28a745',
                cursor: badgeLoading ? 'not-allowed' : 'pointer',
                padding: '4px 6px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title={badgeLoading ? "Lade Badge-Daten..." : "Badge-Positionen neu berechnen"}
            >
              <FiRefreshCw 
                size={12} 
                style={{
                  animation: badgeLoading ? 'spin 1s linear infinite' : 'none'
                }}
              />
            </button>
          )}
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '16px',
              cursor: 'pointer',
              color: '#6c757d',
              padding: '0',
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Schließen"
          >
            ×
          </button>
        </div>
      </div>
      <div style={{ padding: '12px', position: 'relative' }}>
        {/* Badge-Fehleranzeige */}
        {badgeError && taskData?.evidenceType === 'moodle-badge' && (
          <div style={{
            backgroundColor: '#f8d7da',
            border: '1px solid #f1aeb5',
            borderRadius: '4px',
            padding: '8px 12px',
            marginBottom: '12px',
            fontSize: '13px',
            color: '#721c24'
          }}>
            <strong>Fehler beim Laden der Badge-Daten:</strong> {badgeError}
          </div>
        )}
        
        {/* Badge-Lade-Anzeige */}
        {badgeLoading && taskData?.evidenceType === 'moodle-badge' && (
          <div style={{
            backgroundColor: '#d1ecf1',
            border: '1px solid #b8daff',
            borderRadius: '4px',
            padding: '8px 12px',
            marginBottom: '12px',
            fontSize: '13px',
            color: '#0c5460',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <FiRefreshCw 
              size={14} 
              style={{
                animation: 'spin 1s linear infinite'
              }}
            />
            Lade Badge-Daten von Moodle...
          </div>
        )}
        
        {filteredStudents.length > 0 ? (
          <div style={{ position: 'relative', zIndex: 1 }}> {/* Niedrigerer z-Index für die Tabelle */}
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: '14px',
              tableLayout: 'fixed', // Ermöglicht feste Spaltenbreiten
              position: 'relative',
              zIndex: 1
            }}>
              <thead>
                <tr>
                  <th 
                    onClick={() => !isLearner && handleSort('vorname')} // Sortierung nur für Lehrpersonen
                    style={{ 
                      textAlign: 'left', 
                      padding: '8px 4px',
                      height: '37px', // Explizite Höhe
                      borderBottom: '1px solid #dee2e6',
                      fontWeight: 'bold',
                      fontSize: '13px',
                      color: '#495057',
                      cursor: isLearner ? 'default' : 'pointer', // Kein Pointer für Lernende
                      userSelect: 'none',
                      position: 'relative',
                      width: '35%', // Feste Breite für Vorname
                      maxWidth: '35%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={isLearner ? "Vorname" : "Klicken zum Sortieren"}
                  >
                    Vorname {!isLearner && <span style={{ fontSize: '10px', marginLeft: '4px' }}>{getSortIcon('vorname')}</span>}
                  </th>
                  <th 
                    onClick={() => !isLearner && handleSort('nachname')} // Sortierung nur für Lehrpersonen
                    style={{ 
                      textAlign: 'left', 
                      padding: '8px 4px',
                      height: '37px', // Explizite Höhe
                      borderBottom: '1px solid #dee2e6',
                      fontWeight: 'bold',
                      fontSize: '13px',
                      color: '#495057',
                      cursor: isLearner ? 'default' : 'pointer', // Kein Pointer für Lernende
                      userSelect: 'none',
                      position: 'relative',
                      width: '35%', // Feste Breite für Nachname
                      maxWidth: '35%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={isLearner ? "Nachname" : "Klicken zum Sortieren"}
                  >
                    Nachname {!isLearner && <span style={{ fontSize: '10px', marginLeft: '4px' }}>{getSortIcon('nachname')}</span>}
                  </th>
                  <th 
                    onClick={() => !isLearner && handleSort('klasse')} // Sortierung nur für Lehrpersonen
                    style={{ 
                      textAlign: 'left', 
                      padding: '8px 4px',
                      height: '37px', // Explizite Höhe
                      borderBottom: '1px solid #dee2e6',
                      fontWeight: 'bold',
                      fontSize: '13px',
                      color: '#495057',
                      cursor: isLearner ? 'default' : 'pointer', // Kein Pointer für Lernende
                      userSelect: 'none',
                      position: 'relative',
                      width: '30%', // Feste Breite für Klasse
                      maxWidth: '30%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={isLearner ? "Klasse" : "Klicken zum Sortieren"}
                  >
                    Klasse {!isLearner && <span style={{ fontSize: '10px', marginLeft: '4px' }}>{getSortIcon('klasse')}</span>}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents.map((student, index) => {


                  // Berechne das Badge-Fälligkeitsdatum (Task-Start + evidenceDueDays)
                  const taskStartDate = taskData?.startDate instanceof Date ? taskData.startDate : new Date(taskData?.startDate);
                  const badgeDueDate = taskData && taskData.startDate && taskData.evidenceDueDays 
                    ? new Date(taskStartDate.getTime() + (parseInt(taskData.evidenceDueDays) * 24 * 60 * 60 * 1000))
                    : null;
                  
                  // Prüfe, ob das aktuelle Datum größer ist als das Badge-Fälligkeitsdatum
                  const currentDate = new Date();
                  const isBadgeOverdue = badgeDueDate && currentDate > badgeDueDate;

                  // Spezielle Behandlung für nicht-zugewiesene Lernende
                  const isNotAssigned = student.isNotAssigned;

                  // Hilfsfunktionen für E-Mail-Vergleich (DRY-Prinzip)
                  const normalizeEmail = (email) => {
                    if (!email) return '';
                    const parts = email.split('.');
                    if (parts.length > 2) {
                      const lastValidPartIndex = parts.findIndex((part, index) => 
                        index >= 2 && /^\d+$/.test(part)
                      );
                      if (lastValidPartIndex > 0) {
                        return parts.slice(0, lastValidPartIndex).join('.');
                      }
                    }
                    return email;
                  };

                  const isEmailSimilar = (email1, email2) => {
                    if (!email1 || !email2) return false;
                    if (email1 === email2) return true;
                    
                    const getLocalPart = (email) => {
                      const atIndex = email.indexOf('@');
                      return atIndex > 0 ? email.substring(0, atIndex) : email;
                    };
                    
                    const local1 = getLocalPart(email1).toLowerCase();
                    const local2 = getLocalPart(email2).toLowerCase();
                    
                    if (local1 === local2) return true;
                    
                    const normalize = (str) => str.replace(/[._]/g, '');
                    const normalized1 = normalize(local1);
                    const normalized2 = normalize(local2);
                    
                    return normalized1 === normalized2;
                  };

                  // Finde den passenden Moodle-User für diesen Student
                  // Versuche verschiedene E-Mail-Feldnamen und login
                  const studentEmail = student.email || student.Email || student.emailAdresse || student.mail || student.e_mail;
                  const studentLogin = student.login;
                  
                  const matchingUser = userBadgeData && userBadgeData.find(user => {
                    // Prüfe zuerst E-Mail-basierte Matches, falls vorhanden
                    if (studentEmail) {
                      const normalizedStudentEmail = normalizeEmail(studentEmail);
                      const normalizedUserEmail = normalizeEmail(user.email);
                      const normalizedUserUsername = normalizeEmail(user.username);
                      
                      if (normalizedUserEmail === normalizedStudentEmail || 
                          normalizedUserUsername === normalizedStudentEmail ||
                          isEmailSimilar(normalizedUserEmail, normalizedStudentEmail) || 
                          isEmailSimilar(normalizedUserUsername, normalizedStudentEmail)) {
                        return true;
                      }
                    }
                    
                    // Login-basierte Matches als Fallback (unabhängig von E-Mail)
                    if (studentLogin) {
                      const normalizedStudentLogin = normalizeEmail(studentLogin);
                      const normalizedUserEmail = normalizeEmail(user.email);
                      const normalizedUserUsername = normalizeEmail(user.username);
                      
                      if (normalizedUserEmail === normalizedStudentLogin || 
                          normalizedUserUsername === normalizedStudentLogin ||
                          isEmailSimilar(normalizedUserEmail, normalizedStudentLogin) || 
                          isEmailSimilar(normalizedUserUsername, normalizedStudentLogin)) {
                        return true;
                      }
                    }
                    
                    return false;
                  });

                  // Prüfe Badge-Status für diesen Student
                  let badgeStatus = 'none'; // 'none', 'onTime', 'late'
                  
                  if (matchingUser && matchingUser.badges && matchingUser.badges.length > 0) {
                    // Finde das früheste relevante Badge für diese Task
                    const relevantBadges = matchingUser.badges.filter(badge => {
                      return taskData?.evidenceData?.badges?.some(taskBadge => 
                        taskBadge.id === badge.badgeId || taskBadge.id === badge.badgeId.toString()
                      );
                    });
                    
                    if (relevantBadges.length > 0) {
                      // Sortiere Badges nach Ausstellungsdatum (frühestes zuerst)
                      const sortedBadges = relevantBadges.sort((a, b) => {
                        const dateA = a.issuedDate instanceof Date ? a.issuedDate : new Date(a.issuedDate);
                        const dateB = b.issuedDate instanceof Date ? b.issuedDate : new Date(b.issuedDate);
                        return dateA.getTime() - dateB.getTime();
                      });
                      
                      // Verwende das früheste Badge für die Bewertung
                      const earliestBadge = sortedBadges[0];
                      const badgeDate = earliestBadge.issuedDate instanceof Date 
                        ? earliestBadge.issuedDate 
                        : new Date(earliestBadge.issuedDate);
                      
                      if (badgeDueDate) {
                        badgeStatus = badgeDate <= badgeDueDate ? 'onTime' : 'late';
                      } else {
                        badgeStatus = 'onTime'; // Wenn kein Due Date definiert, gilt Badge als pünktlich
                      }
                    }
                  }

                  // Definiere die Textfarbe basierend auf Badge-Status
                  let textColor = '#495057'; // Standard-Grau
                  let fontWeight = 'normal';
                  
                  if (isNotAssigned) {
                    // Nicht-zugewiesener Lernender -> bläulich-grau
                    textColor = '#6c757d';
                    fontWeight = 'normal';
                  } else if (badgeStatus === 'late') {
                    // Student hat Badge, aber zu spät erhalten -> orange
                    textColor = '#fd7e14'; // Bootstrap Orange
                    fontWeight = '600';
                  } else if (badgeStatus === 'onTime') {
                    // Student hat Badge rechtzeitig -> grün
                    textColor = '#28a745'; // Bootstrap Grün
                    fontWeight = '600';
                  } else if (badgeStatus === 'none' && isBadgeOverdue) {
                    // Kein Badge und Badge-Frist ist verstrichen -> rot
                    textColor = '#dc3545'; // Bootstrap Rot
                    fontWeight = '600';
                  }

                  return (
                  <tr key={student.id || index} style={{
                    backgroundColor: isNotAssigned 
                      ? '#e9ecef' // Helleres Grau für nicht-zugewiesene Lernende
                      : (index % 2 === 0 ? '#f8f9fa' : 'white'),
                    height: '31px', // Explizite Höhe
                    position: 'relative',
                    opacity: isNotAssigned ? 0.8 : 1 // Leicht transparenter für nicht-zugewiesene
                  }}>
                    <td style={{ 
                      textAlign: 'left', 
                      padding: '6px 4px',
                      borderBottom: '1px solid #f1f3f4',
                      width: '35%',
                      maxWidth: '35%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: textColor,
                      fontWeight: fontWeight,
                      fontStyle: isNotAssigned ? 'italic' : 'normal'
                    }}
                    title={isNotAssigned ? `${student.vorname} (nicht zugeordnet)` : student.vorname} // Erweiteter Tooltip
                    >
                      {student.vorname}
                    </td>
                    <td style={{ 
                      textAlign: 'left', 
                      padding: '6px 4px',
                      borderBottom: '1px solid #f1f3f4',
                      width: '35%',
                      maxWidth: '35%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: textColor,
                      fontWeight: fontWeight,
                      fontStyle: isNotAssigned ? 'italic' : 'normal'
                    }}
                    title={isNotAssigned ? `${student.nachname} (nicht zugeordnet)` : student.nachname} // Erweiteter Tooltip
                    >
                      {student.nachname}
                    </td>
                    <td style={{ 
                      textAlign: 'left', 
                      padding: '6px 4px',
                      borderBottom: '1px solid #f1f3f4',
                      width: '30%',
                      maxWidth: '30%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: textColor,
                      fontWeight: fontWeight,
                      fontStyle: isNotAssigned ? 'italic' : 'normal'
                    }}
                    title={isNotAssigned ? `${student.klasse} (nicht zugeordnet)` : student.klasse} // Erweiteter Tooltip
                    >
                      {student.klasse}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            
            
            {/* Vereinfachter Flaggen-Container für alle Badges */}
            {taskData && filteredStudents.map((student, index) => {
              // Verwende feste Höhen, die mit den CSS-definierten Höhen übereinstimmen
              const headerHeight = 37; // Empirisch gemessen: 8px + 8px padding + Texthöhe + Border
              const rowHeight = 31; // Empirisch gemessen: 6px + 6px padding + Texthöhe + Border
              
              return (
                <div 
                  key={`flags-${student.id || index}`}
                  style={{
                    position: 'absolute',
                    top: `${headerHeight + (index * rowHeight)}px`,
                    left: '0px', // Badges positionieren sich relativ zu diesem Container
                    width: `${(taskData?.taskWidth || 100)}%`, // Breite entspricht der MovableBar-Breite
                    height: `${rowHeight}px`,
                    pointerEvents: 'auto', // Ermöglicht Tooltips und Klick-Events
                    zIndex: 1000, // Sicherstellt dass Flags über der Tabelle liegen
                    overflow: 'visible' // Erlaubt Badges außerhalb des Container-Bereichs
                  }}
                >
                  <StudentFlags
                    student={student}
                    taskData={taskData}
                    containerWidth={300} // InfoPanel-Breite - Badges werden relativ zur Task-Position berechnet
                    rowHeight={rowHeight}
                    refreshSeed={refreshSeed}
                    userBadgeData={userBadgeData}
                    showOutsideBadges={true} // Zeigt alle Badges an (innen und außen)
                    onFlagClick={(flag, student) => {
                      // Flag-Click für Debugging
                      // Hier könnte später ein Modal geöffnet oder eine andere Aktion ausgeführt werden
                    }}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ 
            textAlign: 'center', 
            color: '#6c757d', 
            fontStyle: 'italic',
            padding: '20px 0',
            fontSize: '14px'
          }}>
            {isLearner 
              ? 'Sie sind diesem Nachweis noch nicht zugeordnet. Falls Sie zugeordnet werden, erscheinen hier Ihre Badge-Fortschritte.'
              : 'Keine Lernenden ausgewählt. Klicken Sie auf das + Icon, um Lernende hinzuzufügen.'
            }
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskInfoPanel;